import React, { createContext, useContext, useEffect, useState, useRef } from 'react';
import { onAuthStateChanged, signOut } from 'firebase/auth';
import { doc, onSnapshot, getDoc, collection, query, where, getDocs } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { auth, db, functions } from '../../utils/firebase';
import { collectionPath } from '../../utils/firestoreCollections';
import { logLogin } from '../../utils/logger';

const AuthContext = createContext();

export function AuthProvider({ children, impersonatedUser }) {
    const [currentUser, setCurrentUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const [disabledMessage, setDisabledMessage] = useState(false);
    // Track previous user to detect login/logout transitions
    const prevUserRef = useRef(null);

    // When impersonating, pages see the impersonated user via useAuth()
    const effectiveUser = impersonatedUser || currentUser;

    useEffect(() => {
        let unsubUser = null;
        const unsubscribe = onAuthStateChanged(auth, async (user) => {
            if (unsubUser) {
                unsubUser(); // Clean up previous listener
                unsubUser = null;
            }
            
            const prevUser = prevUserRef.current;
            
            if (user) {
                // Helper: find user doc by UID (direct lookup) or email (fallback query)
                const findUserDoc = async () => {
                    // First try direct lookup by UID as document ID
                    const uidDoc = await getDoc(doc(db, collectionPath('users', user.uid)));
                    if (uidDoc.exists()) return { ref: uidDoc.ref, data: uidDoc.data(), needsMigration: false };
                    
                    // Fallback: query by email for admin-created users with non-UID doc IDs
                    if (user.email) {
                        const emailQuery = query(
                            collection(db, collectionPath('users')),
                            where('email', '==', user.email)
                        );
                        const emailSnap = await getDocs(emailQuery);
                        if (!emailSnap.empty) {
                            const matchedDoc = emailSnap.docs[0];
                            return { ref: matchedDoc.ref, data: matchedDoc.data(), needsMigration: true, oldDocId: matchedDoc.id };
                        }
                    }
                    return null;
                };

                // Check if user has disabled role BEFORE setting up listeners
                try {
                    const found = await findUserDoc();
                    if (found && found.data.role === 'disabled') {
                        await signOut(auth);
                        setCurrentUser(null);
                        setDisabledMessage(true);
                        setLoading(false);
                        prevUserRef.current = null;
                        return;
                    }
                    
                    // Migrate: copy doc to UID-keyed path and delete old doc
                    if (found && found.needsMigration) {
                        await httpsCallable(functions, 'claimUserProfile')({});
                    } else if (found && !found.data.uid) {
                        await httpsCallable(functions, 'claimUserProfile')({});
                    }
                } catch (e) {
                    console.warn('[AuthContext] Error during user doc lookup/migration:', e);
                    // Don't block login — fall through to listener
                }

                setDisabledMessage(false);

                const isNewLogin = !prevUser && user.uid;
                let loginLogged = false;
                
                // Prevent duplicate login logs from HMR (dev mode) — track in sessionStorage
                const loginSessionKey = `login_logged_${user.uid}`;
                const alreadyLoggedThisSession = sessionStorage.getItem(loginSessionKey);
                
                const userRef = doc(db, collectionPath('users', user.uid));
                unsubUser = onSnapshot(userRef, (userDoc) => {
                    const userData = userDoc.exists() ? userDoc.data() : {};
                    setCurrentUser({
                        ...user,
                        ...userData,
                    });
                    setLoading(false);

                    // Log successful login once with Firestore displayName
                    if (isNewLogin && !loginLogged && !alreadyLoggedThisSession) {
                        loginLogged = true;
                        sessionStorage.setItem(loginSessionKey, Date.now().toString());
                        logLogin({ ...user, displayName: userData.displayName || user.displayName }).catch(e => 
                            console.warn('Failed to log login event:', e)
                        );
                    }
                });
                
                // Reset overdue notifications on login
                try {
                    await fetch(`https://us-central1-${process.env.REACT_APP_FIREBASE_PROJECT_ID}.cloudfunctions.net/resetOverdueNotificationsOnLogin`, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({ userId: user.uid })
                    });
                } catch (error) {
                    console.warn('Failed to reset overdue notifications:', error);
                    // Don't block login on failure
                }
            } else {
                // User logged out - logging is handled in App.jsx handleLogout before signOut
                setCurrentUser(null);
                setLoading(false);
                // Clear login-logged session key so next real login is tracked
                if (prevUser?.uid) {
                    sessionStorage.removeItem(`login_logged_${prevUser.uid}`);
                }
            }
            
            // Update previous user reference
            prevUserRef.current = user;
        });
        return () => {
            unsubscribe();
            if (unsubUser) unsubUser();
        };
    }, []);

    return (
        <AuthContext.Provider value={{
            currentUser: effectiveUser,
            authUser: currentUser,
            impersonatedUser,
            setCurrentUser,
            disabledMessage,
            setDisabledMessage
        }}>
            {!loading && children}
        </AuthContext.Provider>
    );
}

export function useAuth() {
    return useContext(AuthContext);
}